import {
  COMPONENT_TYPE_ADULT_GUEST,
  COMPONENT_TYPE_CHILD_GUEST,
  COMPONENT_TYPE_GUEST,
  Form,
  GUEST_TYPES,
  GuestInfo,
  Guests,
} from '@entities/leadCollector';
import { FormFieldGroup } from '../FormFieldGroup';
import { useEffect } from 'react';
import * as stylex from '@stylexjs/stylex';
import { styles } from './styles';

interface FormGuestsProps {
  form?: Form;
  isCanEdit: boolean;
  value: Guests;
  onChange: (guests: Guests) => void;
}

const MAIN_GUEST_OFFSET = 1;

export const FormGuests = ({ isCanEdit, form, value, onChange }: FormGuestsProps) => {
  const hasExtra = (extraCount?: number) => !!extraCount;
  const shouldShowAdultGuest = hasExtra(form?.maxExtraAdults);
  const shouldShowChildGuest = hasExtra(form?.maxExtraChildren);

  const onChangeGuest = (guest: GuestInfo, guestType: GUEST_TYPES, index?: number) => {
    switch (guestType) {
      case COMPONENT_TYPE_GUEST:
        value.mainGuest = guest;
        break;
      case COMPONENT_TYPE_ADULT_GUEST:
        if (index !== undefined && value.adultGuest?.[index]) {
          value.adultGuest[index] = guest;
        }

        break;
      case COMPONENT_TYPE_CHILD_GUEST:
        if (index !== undefined && value.childrenGuest?.[index]) {
          value.childrenGuest[index] = guest;
        }

        break;
    }

    onChange({ ...value });
  };

  const onDeleteGuest = (guestType: GUEST_TYPES, index: number) => {
    switch (guestType) {
      case COMPONENT_TYPE_ADULT_GUEST:
        value.adultGuest?.splice(index, 1);
        break;
      case COMPONENT_TYPE_CHILD_GUEST:
        value.childrenGuest?.splice(index, 1);
        break;
    }

    onChange(value);
  };

  useEffect(() => {
    if (isCanEdit) {
      value.adultGuest = [];
      value.childrenGuest = [];
      onChange(value);
    }
  }, [isCanEdit]);

  const hasAdditionalGuests = value.adultGuest?.length || value.childrenGuest?.length;

  const mainGuestTitle = value.mainGuestTitle || 'Основной гость *';
  const adultGuestTitle = value.adultGuestTitle || 'Взрослый гость *';
  const childGuestTitle = value.childGuestTitle || 'Маленький гость *';

  const handleTitleChange = (guestType: GUEST_TYPES, newTitle: string) => {
    const updatedValue = { ...value };
    switch (guestType) {
      case COMPONENT_TYPE_GUEST:
        updatedValue.mainGuestTitle = newTitle;
        break;
      case COMPONENT_TYPE_ADULT_GUEST:
        updatedValue.adultGuestTitle = newTitle;
        break;
      case COMPONENT_TYPE_CHILD_GUEST:
        updatedValue.childGuestTitle = newTitle;
        break;
    }
    onChange(updatedValue);
  };

  return (
    <div {...stylex.props(styles.container)}>
      <FormFieldGroup
        title={mainGuestTitle}
        guestType={COMPONENT_TYPE_GUEST}
        isCanEdit={isCanEdit}
        value={value.mainGuest}
        onChange={onChangeGuest}
        number={hasAdditionalGuests ? 0 : undefined}
        isTitleEditable={!isCanEdit}
        onTitleChange={(newTitle) => handleTitleChange(COMPONENT_TYPE_GUEST, newTitle)}
      />

      {shouldShowAdultGuest &&
        value.adultGuest?.map((guest, index) => (
          <FormFieldGroup
            key={guest?.FIO?.[0]?.instanceId || guest?.additionalFields?.[0]?.instanceId || index}
            title={adultGuestTitle}
            guestType={COMPONENT_TYPE_ADULT_GUEST}
            isCanEdit={isCanEdit}
            value={guest}
            onChange={(guest, guestType) => onChangeGuest(guest, guestType, index)}
            number={index + MAIN_GUEST_OFFSET}
            onDelete={(guestType) => onDeleteGuest(guestType, index)}
            isTitleEditable={!isCanEdit}
            onTitleChange={(newTitle) => handleTitleChange(COMPONENT_TYPE_ADULT_GUEST, newTitle)}
          />
        ))}

      {shouldShowChildGuest &&
        value.childrenGuest?.map((guest, index) => (
          <FormFieldGroup
            key={guest?.FIO?.[0]?.instanceId || guest?.additionalFields?.[0]?.instanceId || index}
            title={childGuestTitle}
            guestType={COMPONENT_TYPE_CHILD_GUEST}
            isCanEdit={isCanEdit}
            value={guest}
            onChange={(guest, guestType) => onChangeGuest(guest, guestType, index)}
            number={(value.adultGuest?.length || 0) + index + MAIN_GUEST_OFFSET}
            onDelete={(guestType) => onDeleteGuest(guestType, index)}
            isTitleEditable={!isCanEdit}
            onTitleChange={(newTitle) => handleTitleChange(COMPONENT_TYPE_CHILD_GUEST, newTitle)}
          />
        ))}
    </div>
  );
};
